import { sortCells } from "./sortCells";
import { Cell, State } from "./types";

const cellIntersects = (a: Cell, b: Cell): boolean =>
  !(a.maxX <= b.minX || a.maxY <= b.minY || a.minX >= b.maxX || a.minY >= b.maxY);
const cellWithin = (a: Cell, b: Cell): boolean =>
  a.minX >= b.minX && a.maxX <= b.maxX && a.minY >= b.minY && a.maxY <= b.maxY;

/**
 * Adds a new cell to the existing set. The new cell is forced into the list and all other cells are
 * adjusted "around it" (either by removing fully eclipsed cells or cropping them to fit).
 * @param oldCells ReadonlyArray<Cell> The old list of cells.
 * @param newCell Cell The new cell to insert into the list.
 */
const addCell = (oldCells: State["cells"], newCell: Cell): State["cells"] => {
  let toReturn: Array<Cell> = [newCell];
  for (const cell of oldCells) {
    // if the cells do not intersect at all, keep "cell" and move on
    if (!cellIntersects(cell, newCell)) {
      toReturn.push(cell);
      continue;
    }
    // if "cell" is completely within the new cell, we can move on (dropping it)
    if (cellWithin(cell, newCell)) {
      continue;
    }
    // the new cell intersects (but doesn't completely assume the old cell)
    // figure out which edges intersect
    const intersections = {
      bottom: newCell.maxY > cell.minY && newCell.maxY < cell.maxY,
      left: newCell.minX > cell.minX && newCell.minX < cell.maxX,
      right: newCell.maxX > cell.minX && newCell.maxX < cell.maxX,
      top: newCell.minY > cell.minY && newCell.minY < cell.maxY,
    };
    if (intersections.bottom) {
      if (intersections.left) {
        toReturn = toReturn.concat([
          {
            ...cell,
            maxX: newCell.minX,
            maxY: newCell.maxY,
          },
          {
            ...cell,
            maxX: newCell.minX,
            minY: newCell.maxY,
          },
          {
            ...cell,
            minX: newCell.minX,
            minY: newCell.maxY,
          },
        ]);
      } else if (intersections.right) {
        toReturn = toReturn.concat([
          {
            ...cell,
            maxY: newCell.maxY,
            minX: newCell.maxX,
          },
          {
            ...cell,
            maxX: newCell.maxX,
            minY: newCell.maxY,
          },
          {
            ...cell,
            minX: newCell.maxX,
            minY: newCell.maxY,
          },
        ]);
      } else {
        toReturn.push({
          ...cell,
          minY: newCell.maxY,
        });
      }
    } else if (intersections.top) {
      if (intersections.left) {
        toReturn = toReturn.concat([
          {
            ...cell,
            maxX: newCell.minX,
            maxY: newCell.minY,
          },
          {
            ...cell,
            maxY: newCell.minY,
            minX: newCell.minX,
          },
          {
            ...cell,
            maxX: newCell.minX,
            minY: newCell.minY,
          },
        ]);
      } else if (intersections.right) {
        toReturn = toReturn.concat([
          {
            ...cell,
            maxX: newCell.maxX,
            maxY: newCell.minY,
          },
          {
            ...cell,
            maxY: newCell.minY,
            minX: newCell.maxX,
          },
          {
            ...cell,
            minX: newCell.maxX,
            minY: newCell.minY,
          },
        ]);
      } else {
        toReturn.push({
          ...cell,
          maxY: newCell.minY,
        });
      }
    } else if (intersections.left) {
      toReturn.push({
        ...cell,
        maxX: newCell.minX,
      });
    } else if (intersections.right) {
      toReturn.push({
        ...cell,
        minX: newCell.maxX,
      });
    }
  }
  return toReturn;
};

export const addCells = (oldCells: State["cells"], newCells: ReadonlyArray<Cell>): State["cells"] =>
  sortCells(newCells.reduce(addCell, oldCells) as Array<Cell>);
